import React, { useState, useEffect, useRef, useCallback } from "react";
import { useChatState } from "../hooks/useChatState";
import ChatWindow from "./ChatWindow";
import TriggerButton from "./TriggerButton";
import "./ChatWidget.css";

const ChatWidget = ({ config }) => {
  const [isOpen, setIsOpen] = useState(config.autoOpen || false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentTheme, setCurrentTheme] = useState(() => {
    if (config.theme === "auto") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return config.theme || "light";
  });
  const widgetRef = useRef(null);

  const {
    messages,
    addMessage,
    updateMessage,
    clearMessages,
    isTyping,
    setIsTyping,
  } = useChatState();

  // Use HTTP instead of WebSocket since backend is Python/FastAPI, not Node.js Socket.IO
  const apiUrl = config.apiUrl || "https://api.bot.cioafrica.co";
  const [isConnected, setIsConnected] = useState(true); // HTTP is always "connected"
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // HTTP-based chat implementation
  const sendMessage = useCallback(
    async (messageData) => {
      setIsLoading(true);
      setError(null);
      setIsTyping(true);

      try {
        const response = await fetch(`${apiUrl}/query`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question: messageData.message,
            session_id: messageData.sessionId,
            history: messageData.history || [],
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.answer) {
          // Add bot response message
          addMessage({
            id: Date.now(),
            content: data.answer,
            role: "assistant",
            timestamp: new Date().toISOString(),
            status: "delivered",
          });
          setIsTyping(false);
        } else {
          throw new Error(data.error || "Unknown error");
        }
      } catch (err) {
        console.error("HTTP chat error:", err);
        setError(err.message);
        addMessage({
          id: Date.now(),
          content: "Sorry, I encountered an error. Please try again.",
          role: "system",
          timestamp: new Date().toISOString(),
          status: "error",
        });
        setIsTyping(false);
      } finally {
        setIsLoading(false);
      }
    },
    [apiUrl, addMessage]
  );

  const reconnect = useCallback(() => {
    setError(null);
    setIsConnected(true);
    console.log("Chatbot reconnected via HTTP");
  }, []);

  const disconnect = useCallback(() => {
    setIsConnected(false);
    console.log("Chatbot disconnected from HTTP");
  }, []);

  // Handle external events
  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    const handleClose = () => setIsOpen(false);

    const widget = widgetRef.current;
    if (widget) {
      widget.addEventListener("chatbot:open", handleOpen);
      widget.addEventListener("chatbot:close", handleClose);

      return () => {
        widget.removeEventListener("chatbot:open", handleOpen);
        widget.removeEventListener("chatbot:close", handleClose);
      };
    }
  }, []);

  // Update unread count when new messages arrive while closed
  useEffect(() => {
    if (!isOpen && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === "assistant") {
        setUnreadCount((prev) => prev + 1);
      }
    }
  }, [messages, isOpen]);

  // Reset unread count when opening
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  const handleSendMessage = useCallback(
    async (content, attachments = []) => {
      if (!content.trim() && attachments.length === 0) return;

      const messageId = Date.now();
      const userMessage = {
        id: messageId,
        content: content.trim(),
        role: "user",
        timestamp: new Date().toISOString(),
        status: "sending",
        attachments,
      };

      addMessage(userMessage);

      try {
        await sendMessage({
          message: content.trim(),
          sessionId: getSessionId(),
          history: messages.slice(-10), // Last 10 messages for context
          attachments,
        });

        updateMessage(messageId, { status: "delivered" });
      } catch (error) {
        console.error("Failed to send message:", error);
        updateMessage(messageId, { status: "error" });
      }
    },
    [messages, addMessage, updateMessage, sendMessage]
  );

  const handleToggleChat = useCallback(() => {
    console.log("🔄 Chat toggle clicked, current state:", isOpen);

    setIsOpen((prev) => {
      const newState = !prev;
      console.log("🔄 Setting chat to:", newState ? "OPEN" : "CLOSED");

      // Mobile body scroll handling
      const isMobile = window.innerWidth <= 480;
      if (isMobile) {
        if (newState) {
          // Prevent body scroll when opening chat
          document.body.style.overflow = "hidden";
          document.body.style.position = "fixed";
          document.body.style.width = "100%";
          document.body.style.height = "100%";
          console.log("📱 Mobile: Body scroll disabled");
        } else {
          // Restore body scroll when closing chat
          document.body.style.overflow = "";
          document.body.style.position = "";
          document.body.style.width = "";
          document.body.style.height = "";
          console.log("📱 Mobile: Body scroll restored");
        }
      }

      return newState;
    });
    setIsMinimized(false);
  }, [isOpen]);

  const handleMinimize = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const handleMaximize = useCallback(() => {
    setIsMinimized(false);
  }, []);

  const getSessionId = () => {
    let sessionId = localStorage.getItem("chatbot-session-id");
    if (!sessionId) {
      sessionId =
        "session_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
      localStorage.setItem("chatbot-session-id", sessionId);
    }
    return sessionId;
  };

  const toggleTheme = useCallback(() => {
    setCurrentTheme((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  // Listen for system theme changes when using auto theme
  useEffect(() => {
    if (config.theme === "auto") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = (e) => {
        setCurrentTheme(e.matches ? "dark" : "light");
      };

      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [config.theme]);

  // Apply theme to document
  useEffect(() => {
    const widget = widgetRef.current;
    if (widget) {
      widget.setAttribute("data-theme", currentTheme);
      widget.setAttribute("data-size", config.size || "large");
    }
  }, [currentTheme, config.size]);

  // Cleanup effect for mobile body scroll
  useEffect(() => {
    return () => {
      // Cleanup: restore body scroll when component unmounts
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
    };
  }, []);

  // Helper function to darken a color for hover state
  const darkenColor = (color, amount = 0.1) => {
    if (!color || !color.startsWith("#")) return color;
    const num = parseInt(color.replace("#", ""), 16);
    const r = Math.max(0, (num >> 16) - Math.round(255 * amount));
    const g = Math.max(0, ((num >> 8) & 0x00ff) - Math.round(255 * amount));
    const b = Math.max(0, (num & 0x0000ff) - Math.round(255 * amount));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  };

  const primaryColor =
    config.customTheme?.[currentTheme]?.primary || config.primaryColor;

  const widgetStyle = {
    "--primary-color": primaryColor,
    "--primary-hover": darkenColor(primaryColor, 0.1),
    "--background": config.customTheme?.[currentTheme]?.background,
    "--surface": config.customTheme?.[currentTheme]?.surface,
    "--text-primary": config.customTheme?.[currentTheme]?.text,
    "--border": config.customTheme?.[currentTheme]?.border,
    "--position": config.position,
  };

  return (
    <div
      ref={widgetRef}
      className="chatbot-widget"
      style={widgetStyle}
      data-position={config.position}
    >
      <TriggerButton
        isOpen={isOpen}
        unreadCount={config.showUnreadBadge ? unreadCount : 0}
        onClick={handleToggleChat}
        config={config}
      />

      {isOpen && (
        <ChatWindow
          isMinimized={isMinimized}
          messages={messages}
          isTyping={isTyping}
          isConnected={isConnected}
          onSendMessage={handleSendMessage}
          onMinimize={handleMinimize}
          onMaximize={handleMaximize}
          onClose={() => setIsOpen(false)}
          config={config}
          currentTheme={currentTheme}
          onToggleTheme={toggleTheme}
        />
      )}
    </div>
  );
};

export default ChatWidget;
